/** Google Drive file/folder picker using @googleworkspace/drive-picker-react. */

import { useState, useCallback, useEffect } from "react";
import { Box, Button, Typography, Paper, IconButton, CircularProgress, Alert, TextField, MenuItem } from "@mui/material";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import FolderIcon from "@mui/icons-material/Folder";
import CloudIcon from "@mui/icons-material/Cloud";
import CloseIcon from "@mui/icons-material/Close";
import LinkOffIcon from "@mui/icons-material/LinkOff";
import { DrivePicker, DrivePickerDocsView } from "@googleworkspace/drive-picker-react";
import { useGDriveCredential } from "@/hooks/useGDriveCredential";
import DriveLabelSelector from "@/components/DriveLabelSelector";
import type { GDrivePickerValue } from "@/types";

interface GDrivePickerProps {
  label: string;
  helperText?: string;
  mode: "file" | "folder";
  value: GDrivePickerValue | null;
  onChange: (v: GDrivePickerValue | null) => void;
  showRange?: boolean;
  mimeFilter?: string;
  showTemplatePicker?: boolean;
  templateType?: string;
}

export default function GDrivePicker({ label, helperText, mode, value, onChange, showRange, mimeFilter, showTemplatePicker, templateType }: GDrivePickerProps) {
  const { isConnected, isLoading, connect, reconnect, getAccessToken, disconnect, credential, googleClientId, scopeStatus } = useGDriveCredential();
  const [oauthToken, setOauthToken] = useState<string | null>(null);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [templates, setTemplates] = useState<{ id: string; name: string }[]>([]);

  // Fetch templates when template picker is enabled and folder selected
  useEffect(() => {
    if (!showTemplatePicker || !value || mode !== "folder") return;
    const fetchTemplates = async () => {
      try {
        const token = await getAccessToken();
        if (!token) return;
        const { getConfig } = await import("@/auth/config");
        const { getUserManager } = await import("@/hooks/useAuth");
        const config = getConfig();
        const um = getUserManager();
        const user = await um?.getUser();
        const jwt = user?.id_token;
        if (!jwt) return;
        const base = config?.apiEndpoint ?? "";
        const res = await fetch(`${base}/reporting/templates?type=${templateType || "document"}`, {
          headers: { Authorization: `Bearer ${jwt}` },
        });
        if (res.ok) {
          const data = await res.json();
          setTemplates(data.templates ?? []);
        }
      } catch { /* ignore */ }
    };
    fetchTemplates();
  }, [showTemplatePicker, value, mode, templateType, getAccessToken]);

  // App ID is the Cloud project number (numeric prefix of the client ID)
  const appId = googleClientId?.split("-")[0] ?? "";

  const openPicker = useCallback(async () => {
    const token = await getAccessToken();
    if (!token) return;
    setOauthToken(token);
    setPickerOpen(true);
  }, [getAccessToken]);

  const handlePicked = useCallback(
    (e: CustomEvent) => {
      setPickerOpen(false);
      const doc = e.detail?.docs?.[0];
      if (doc) {
        onChange({
          appCredentialId: credential?.appCredentialId ?? "",
          fileId: doc.id,
          name: doc.name,
          mimeType: doc.mimeType,
          driveId: doc.driveId ?? undefined,
        });
      }
    },
    [credential, onChange],
  );

  const handleCanceled = useCallback(() => {
    setPickerOpen(false);
  }, []);

  if (isLoading) {
    return (
      <Box>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>{label}</Typography>
        <CircularProgress size={20} />
      </Box>
    );
  }

  if (!isConnected) {
    return (
      <Box>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>{label}</Typography>
        <Button variant="outlined" startIcon={<CloudIcon />} onClick={connect} size="small">
          Connect Google Drive
        </Button>
        {helperText && <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.5 }}>{helperText}</Typography>}
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>{label}</Typography>

      {value ? (
        <Paper variant="outlined" sx={{ display: "flex", alignItems: "center", px: 1.5, py: 1, gap: 1 }}>
          {mode === "folder" ? <FolderIcon color="primary" fontSize="small" /> : <InsertDriveFileIcon color="primary" fontSize="small" />}
          <Typography variant="body2" sx={{ flex: 1 }}>{value.name}</Typography>
          <IconButton size="small" onClick={() => onChange(null)} title="Clear selection">
            <CloseIcon fontSize="small" />
          </IconButton>
        </Paper>
      ) : (
        <Button variant="outlined" startIcon={mode === "folder" ? <FolderIcon /> : <InsertDriveFileIcon />} onClick={openPicker} size="small">
          {mode === "folder" ? "Choose Folder" : "Choose File"}
        </Button>
      )}

      {/* Contextual fields: sheet tab + range (shown after spreadsheet selection) */}
      {value && showRange && value.mimeType?.includes("spreadsheet") && (
        <Box sx={{ display: "flex", gap: 1, mt: 1 }}>
          <TextField
            label="Sheet Tab"
            value={value.sheetName ?? ""}
            onChange={(e) => onChange({ ...value, sheetName: e.target.value || undefined })}
            size="small"
            placeholder="Sheet1"
            sx={{ flex: 1 }}
          />
          <TextField
            label="Range"
            value={value.range ?? ""}
            onChange={(e) => onChange({ ...value, range: e.target.value || undefined })}
            size="small"
            placeholder="A1:D50"
            sx={{ flex: 1 }}
          />
        </Box>
      )}

      {/* Drive Label selector (folder picker only, after folder selection) */}
      {value && mode === "folder" && (
        <DriveLabelSelector
          value={value.labels ?? {}}
          onChange={(labels) => onChange({ ...value, labels })}
        />
      )}

      {/* Template picker (folder picker with showTemplatePicker, after folder selection) */}
      {value && mode === "folder" && showTemplatePicker && (
        <TextField
          select
          label="Template"
          value={value.templateId ?? ""}
          onChange={(e) => onChange({ ...value, templateId: e.target.value || undefined })}
          size="small"
          fullWidth
          sx={{ mt: 1 }}
        >
          <MenuItem value="">Default (organisation template)</MenuItem>
          {templates.length === 0 && (
            <MenuItem disabled value="__none">No templates available — a blank document will be created.</MenuItem>
          )}
          {templates.map((t) => (
            <MenuItem key={t.id} value={t.id}>{t.name}</MenuItem>
          ))}
        </TextField>
      )}

      {helperText && <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.5 }}>{helperText}</Typography>}

      {scopeStatus === "needs_upgrade" && (
        <Alert severity="info" sx={{ mt: 1 }} action={
          <Button color="inherit" size="small" onClick={reconnect}>Reconnect</Button>
        }>
          Expanded access required for Sheets &amp; Docs. Reconnect to grant.
        </Alert>
      )}

      <Button size="small" startIcon={<LinkOffIcon />} onClick={() => disconnect()} sx={{ mt: 0.5, textTransform: "none", color: "text.secondary" }}>
        Disconnect Google Drive
      </Button>

      {/* Picker auto-shows on mount; unmount on pick/cancel to close */}
      {pickerOpen && oauthToken && (
        <DrivePicker
          oauth-token={oauthToken}
          app-id={appId}
          onPicked={handlePicked}
          onCanceled={handleCanceled}
        >
          {mode === "folder" ? (
            <DrivePickerDocsView
              enable-drives="true"
              include-folders="true"
              select-folder-enabled="true"
              mime-types="application/vnd.google-apps.folder"
              mode="LIST"
            />
          ) : (
            <DrivePickerDocsView
              enable-drives="true"
              include-folders="true"
              mode="LIST"
              {...(mimeFilter ? { "mime-types": mimeFilter } : {})}
            />
          )}
        </DrivePicker>
      )}
    </Box>
  );
}
